import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LogOut, Phone, Mail, MapPin, Car, Calendar, Clock,
  CheckCircle2, XCircle, Hourglass, ListChecks, Trash2,
  Search, RefreshCw, AlertCircle, Crown, Lock, Unlock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'proclean_admin_token';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Hourglass },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600 rounded-full blur-3xl opacity-20"></div>
      <div className="relative bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md border border-slate-200">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_ad632560-d0a8-4e29-a8e3-0fdc0eb21a29/artifacts/erjp1y5v_594DED26-5434-47CF-8EE8-EA9BF153DAD6.PNG"
            alt="ProClean"
            className="h-14 w-auto mx-auto mb-4 bg-slate-900 rounded-xl p-2"
          />
          <h1 className="text-2xl font-extrabold text-slate-900">Admin Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">ProClean Mobile Detailing</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              required
              data-testid="admin-password-input"
              className="rounded-xl h-12 border-slate-200"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-6 font-semibold"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        <p className="text-xs text-slate-400 text-center mt-6">
          Forgot the password? Update <code className="text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">ADMIN_PASSWORD</code> in <code className="text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">backend/.env</code>
        </p>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase font-bold tracking-wider text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="text-3xl font-extrabold text-slate-900">{value}</div>
  </div>
);

const BookingCard = ({ booking, onUpdate, onDelete }) => {
  const [updating, setUpdating] = useState(false);
  const status = statusConfig[booking.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const created = new Date(booking.createdAt);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    await onUpdate(booking.id, newStatus);
    setUpdating(false);
  };

  return (
    <div data-testid={`booking-${booking.id}`} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h3 className="text-xl font-bold text-slate-900">{booking.name}</h3>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${status.color}`}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </span>
          </div>
          <p className="text-sm text-slate-500">
            Booked {created.toLocaleDateString('en-AU')} at {created.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button
          data-testid={`delete-${booking.id}`}
          onClick={() => {
            if (window.confirm('Delete this booking?')) onDelete(booking.id);
          }}
          className="text-slate-400 hover:text-red-600 transition-colors p-2"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
        <a href={`tel:${booking.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
          <Phone className="w-4 h-4 text-slate-400" />
          <span className="font-semibold">{booking.phone}</span>
        </a>
        <a href={`mailto:${booking.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600 break-all">
          <Mail className="w-4 h-4 text-slate-400" />
          {booking.email}
        </a>
        <div className="flex items-center gap-2 text-slate-700">
          <Car className="w-4 h-4 text-slate-400" />
          <span className="capitalize">{booking.vehicleType}{booking.vehicleMake ? ` • ${booking.vehicleMake} ${booking.vehicleModel}` : ''}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <MapPin className="w-4 h-4 text-slate-400" />
          <span>{booking.address}</span>
        </div>
        {(booking.preferredDate || booking.preferredTime) && (
          <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span>{booking.preferredDate || 'Any date'} • {booking.preferredTime || 'flexible'}</span>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4">
        <div className="text-xs uppercase font-bold tracking-wider text-blue-700 mb-1">Package</div>
        <div className="font-bold text-slate-900">{booking.selectedPackage}</div>
        {booking.selectedAddOns && booking.selectedAddOns.length > 0 && (
          <div className="mt-2 pt-2 border-t border-blue-100">
            <div className="text-xs uppercase font-bold tracking-wider text-blue-700 mb-1">Add-ons</div>
            <div className="flex flex-wrap gap-1.5">
              {booking.selectedAddOns.map((a, i) => (
                <span key={i} className="inline-block bg-white border border-blue-200 text-slate-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  + {a}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {booking.specialRequests && (
        <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4 text-sm text-slate-700">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Notes</div>
          {booking.specialRequests}
        </div>
      )}

      <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-100">
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <button
            key={key}
            data-testid={`status-${key}-${booking.id}`}
            disabled={updating || booking.status === key}
            onClick={() => handleStatusChange(key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
              booking.status === key
                ? `${cfg.color} cursor-default`
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
            } ${updating ? 'opacity-50' : ''}`}
          >
            {cfg.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [vipRequests, setVipRequests] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, vipPending: 0, vipApproved: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('bookings'); // 'bookings' | 'vip' | 'maintenance'

  const authHeaders = { headers: { 'X-Admin-Password': token } };

  const fetchData = useCallback(async () => {
    try {
      const [bRes, sRes, vRes, uRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, authHeaders),
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/admin/vip-requests`, authHeaders),
        axios.get(`${API}/admin/maintenance-unlocks`, authHeaders),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
      setVipRequests(vRes.data);
      setUnlocks(uRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired');
        onLogout();
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, authHeaders);
      toast.success(`Marked as ${status}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeaders);
      toast.success('Booking deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleVipUpdate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/vip-requests/${id}`, { status }, authHeaders);
      toast.success(`VIP request ${status}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleVipDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/vip-requests/${id}`, authHeaders);
      toast.success('VIP request deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUnlock = async (email, phone) => {
    try {
      await axios.post(`${API}/admin/maintenance-unlocks`, { email, phone }, authHeaders);
      toast.success('Maintenance access unlocked');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    }
  };

  const handleRevoke = async (id) => {
    try {
      await axios.delete(`${API}/admin/maintenance-unlocks/${id}`, authHeaders);
      toast.success('Maintenance access revoked');
      fetchData();
    } catch (err) {
      toast.error('Failed to revoke');
    }
  };

  const filteredBookings = bookings.filter((b) => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      b.name.toLowerCase().includes(s) ||
      b.email.toLowerCase().includes(s) ||
      b.phone.includes(s) ||
      b.selectedPackage.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-slate-900 rounded-lg p-1.5">
              <img
                src="https://customer-assets.emergentagent.com/job_ad632560-d0a8-4e29-a8e3-0fdc0eb21a29/artifacts/erjp1y5v_594DED26-5434-47CF-8EE8-EA9BF153DAD6.PNG"
                alt="ProClean"
                className="h-7 w-auto"
              />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 text-base sm:text-lg">Admin Dashboard</h1>
              <p className="text-xs text-slate-500 hidden sm:block">Manage bookings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn" className="rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={onLogout} variant="outline" size="sm" data-testid="logout-btn" className="rounded-lg">
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Bookings" value={stats.total} icon={ListChecks} color="bg-slate-100 text-slate-700" />
          <StatCard label="Pending" value={stats.pending} icon={Hourglass} color="bg-yellow-100 text-yellow-700" />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="bg-green-100 text-green-700" />
          <StatCard label="VIP Members" value={stats.vipApproved} icon={Crown} color="bg-amber-100 text-amber-700" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white p-1.5 rounded-2xl border border-slate-200 w-fit">
          <button
            data-testid="tab-bookings"
            onClick={() => setTab('bookings')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              tab === 'bookings' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <ListChecks className="w-4 h-4 inline mr-2" />
            Bookings
          </button>
          <button
            data-testid="tab-vip"
            onClick={() => setTab('vip')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all relative ${
              tab === 'vip' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Crown className="w-4 h-4 inline mr-2" />
            VIP Members
            {stats.vipPending > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {stats.vipPending}
              </span>
            )}
          </button>
          <button
            data-testid="tab-maintenance"
            onClick={() => setTab('maintenance')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              tab === 'maintenance' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Unlock className="w-4 h-4 inline mr-2" />
            Maintenance Access
            <span className="ml-2 bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unlocks.length}</span>
          </button>
        </div>

        {tab === 'bookings' ? (
          <>
            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Search by name, email, phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-xl border-slate-200"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto">
                {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((f) => (
                  <button
                    key={f}
                    data-testid={`filter-${f}`}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                      filter === f
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {f === 'all' ? 'All' : statusConfig[f]?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Bookings list */}
            {loading ? (
              <div className="text-center py-20 text-slate-500">Loading bookings...</div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-semibold">
                  {bookings.length === 0 ? 'No bookings yet' : 'No bookings match your filters'}
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  {bookings.length === 0 ? 'New booking requests will appear here.' : 'Try changing your search or filter.'}
                </p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-4">
                {filteredBookings.map((b) => (
                  <BookingCard key={b.id} booking={b} onUpdate={handleUpdateStatus} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </>
        ) : tab === 'maintenance' ? (
          <MaintenanceTab unlocks={unlocks} onUnlock={handleUnlock} onRevoke={handleRevoke} loading={loading} />
        ) : (
          <VIPTab
            vipRequests={vipRequests}
            onUpdate={handleVipUpdate}
            onDelete={handleVipDelete}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

const VIPTab = ({ vipRequests, onUpdate, onDelete, loading }) => {
  const vipStatusConfig = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    approved: { label: 'Approved', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200' },
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Loading...</div>;

  if (vipRequests.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
        <Crown className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-semibold">No VIP requests yet</p>
        <p className="text-slate-400 text-sm mt-1">Customers requesting VIP status will appear here.</p>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {vipRequests.map((v) => {
        const s = vipStatusConfig[v.status] || vipStatusConfig.pending;
        const created = new Date(v.createdAt);
        return (
          <div key={v.id} data-testid={`vip-${v.id}`} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <h3 className="text-xl font-bold text-slate-900">{v.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${s.color}`}>
                    {s.label}
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Requested {created.toLocaleDateString('en-AU')} at {created.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                data-testid={`vip-delete-${v.id}`}
                onClick={() => {
                  if (window.confirm('Delete this VIP request?')) onDelete(v.id);
                }}
                className="text-slate-400 hover:text-red-600 transition-colors p-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 mb-4 text-sm">
              <a href={`tel:${v.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
                <Phone className="w-4 h-4 text-slate-400" />
                <span className="font-semibold">{v.phone}</span>
              </a>
              <a href={`mailto:${v.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600 break-all">
                <Mail className="w-4 h-4 text-slate-400" />
                {v.email}
              </a>
            </div>

            {v.notes && (
              <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4 text-sm text-slate-700">
                <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Notes</div>
                {v.notes}
              </div>
            )}

            <div className="flex gap-2 flex-wrap pt-3 border-t border-slate-100">
              {['pending', 'approved', 'rejected'].map((st) => (
                <button
                  key={st}
                  data-testid={`vip-status-${st}-${v.id}`}
                  disabled={v.status === st}
                  onClick={() => onUpdate(v.id, st)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                    v.status === st
                      ? `${vipStatusConfig[st].color} cursor-default`
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                  }`}
                >
                  {vipStatusConfig[st].label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MaintenanceTab = ({ unlocks, onUnlock, onRevoke, loading }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email && !phone) {
      toast.error('Enter an email or phone number');
      return;
    }
    await onUnlock(email, phone);
    setEmail('');
    setPhone('');
  };

  return (
    <div>
      {/* Unlock form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
            <Unlock className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Unlock Maintenance Detail</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Customers listed here can book the $150 Maintenance Detail. Completing a Premium Detail unlocks them automatically — or add someone manually below.
        </p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="unlock-email"
            type="email"
            placeholder="Customer email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-xl h-12 border-slate-200 flex-1"
          />
          <Input
            data-testid="unlock-phone"
            placeholder="Customer phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="rounded-xl h-12 border-slate-200 flex-1"
          />
          <Button data-testid="unlock-submit" type="submit" className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-12 px-6 font-semibold">
            <Unlock className="w-4 h-4 mr-2" />
            Unlock Access
          </Button>
        </form>
      </div>

      {/* Unlocked customers */}
      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading...</div>
      ) : unlocks.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">No maintenance customers yet</p>
          <p className="text-slate-400 text-sm mt-1">They'll appear here automatically when you mark a Premium Detail as completed.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {unlocks.map((u) => {
            const created = new Date(u.createdAt);
            return (
              <div key={u.id} data-testid={`unlock-${u.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Unlock className="w-4 h-4 text-green-600" />
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                      u.source === 'premium-completed'
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}>
                      {u.source === 'premium-completed' ? 'Premium completed' : 'Added manually'}
                    </span>
                  </div>
                  {u.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {u.email}
                    </div>
                  )}
                  {u.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-700 mt-1">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {u.phone}
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-2">
                    Since {created.toLocaleDateString('en-AU')}
                  </div>
                </div>
                <button
                  data-testid={`revoke-${u.id}`}
                  onClick={() => { if (window.confirm('Revoke maintenance access for this customer?')) onRevoke(u.id); }}
                  className="text-slate-400 hover:text-red-600 transition-colors p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.info('Logged out');
  };

  return (
    <>
      {token ? (
        <Dashboard token={token} onLogout={handleLogout} />
      ) : (
        <LoginScreen onLogin={setToken} />
      )}
      <Toaster position="top-center" richColors />
    </>
  );
};

export default Admin;
